
import { useState } from 'react';

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d45eckngaed3g6l51t00', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('お申し込みありがとうございます。担当者よりご連絡いたします。');
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        setSubmitStatus('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      setSubmitStatus('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-lg md:text-2xl font-bold text-pink-600" style={{ fontFamily: '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif' }}>
            マイペットハウス
          </h1>
          <div className="flex gap-4">
            <button 
              onClick={() => document.getElementById('reservation-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-pink-500 text-white px-4 md:px-6 py-2 rounded-full hover:bg-pink-600 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              モデルハウス見学予約
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url('https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/96625ba2ba5f9e853639b9eddc17d407.jpeg')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
            秋田県湯沢市<br />
            <br />
            あなたとペットだけの<br />
            <span className="text-pink-600">安心できる住まい</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            高断熱・防音仕様で、ペットも一年中快適。<br />
            最短1日で設置可能な9坪のコンパクト住宅
          </p>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">コンセプト</h3>
            <p className="text-lg text-gray-600 leading-relaxed text-left">
              「マイペットハウス」は、ペットと暮らしたいおひとり様のためのコンパクト住宅。<br />
              高断熱仕様でペットも一年中快適、最短1日で建築可能なユニットハウス。<br />
              9坪にバス・トイレ・キッチンを完備し、低コストで自分とペットの居場所を実現。<br />
              静かで心地よい小さなマイホームです。
            </p>
          </div>
        </div>
      </section>

      {/* Model House Photos */}
      <section className="py-20 bg-pink-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">モデルハウス</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/ba78b2c833992480da1f0c477ff2ed8a.jpeg"
                alt="モデルハウス外観"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-4">
                <h4 className="font-bold text-gray-800">外観デザイン</h4>
                <p className="text-gray-600 text-sm">シンプルで美しい外観</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/1e41b1de55a363105ff3a88020ed0984.jpeg"
                alt="モデルハウス内装"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-4">
                <h4 className="font-bold text-gray-800">内装デザイン</h4>
                <p className="text-gray-600 text-sm">ペットと快適に過ごせる空間</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/7755634a9ec0418b3b83e0cdd71f7c11/ee93017269838be6f8eca96cafc75822.jpeg"
                alt="水回り設備"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-4">
                <h4 className="font-bold text-gray-800">充実の設備</h4>
                <p className="text-gray-600 text-sm">バス・トイレ・キッチン完備</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">住宅仕様</h3>
          <div className="max-w-4xl mx-auto bg-pink-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-gray-800 mb-4">基本仕様</h4>
                <ul className="space-y-2 text-gray-600">
                  <li><strong>商品名：</strong>マイペットハウス S</li>
                  <li><strong>用途：</strong>コンテナ住宅</li>
                  <li><strong>サイズ：</strong>2640mm×8,190mm、H2,400mm</li>
                  <li><strong>面積：</strong>約9坪（確認申請が必要）</li>
                  <li><strong>屋根：</strong>ガルバリウム合板</li>
                  <li><strong>外壁：</strong>金属サイディング仕上げ</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-800 mb-4">内装・設備</h4>
                <ul className="space-y-2 text-gray-600">
                  <li><strong>天井：</strong>化粧ボード仕上げ</li>
                  <li><strong>壁：</strong>化粧ボード仕上げ</li>
                  <li><strong>床：</strong>フローリング柄塩ビタイル仕上げ</li>
                  <li><strong>電気設備：</strong>コンセント、一部照明あり</li>
                  <li><strong>給排水設備：</strong>ユニットバス、キッチン、水洗トイレ、洗面台あり</li>
                  <li><strong>保証：</strong>瑕疵保障保険適用あり</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-pink-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium">
                1951年創業　75年の実績をもつ住宅会社
              </p>
              <p className="text-xs text-gray-600 mt-1">
                瑕疵保証保険適用・建設業許可・確認申請対応済みで安心してお任せいただけます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Floor Plan */}
      <section className="py-20 bg-pink-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">間取り図</h3>
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/68db251a5581adf514d81c746b1bd432.jpg"
              alt="間取り図"
              className="w-full h-auto rounded-lg object-cover object-top"
            />
            <p className="text-center text-gray-600 mt-4">
              効率的な9坪レイアウト - ペットとの暮らしを考えた設計
            </p>
          </div>
        </div>
      </section>

      {/* Recommended For */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-12">マイペットハウスをおすすめの方</h3>
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            <div className="bg-pink-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-pink-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-heart-line text-2xl text-pink-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-3">安心できる住まいを求める方</h4>
              <p className="text-gray-600">
                賃貸ではペット可物件が少なく、自分とペットだけの安心できる住まいを持ちたい方
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-3">快適な環境を整えたい方</h4>
              <p className="text-gray-600">
                マイペットハウス モデルハウス 秋田県湯沢市
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-volume-mute-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-3">鳴き声を気にせず暮らしたい方</h4>
              <p className="text-gray-600">
                防音性能が高く、ペットの鳴き声や足音を気にせず安心して暮らせる環境を提供
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* お客様の声セクション */}
      <section className="py-20 bg-pink-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">お客様の声</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20woman%20in%20her%2030s%2C%20office%20worker%2C%20friendly%20smile%2C%20business%20casual%20attire%2C%20soft%20lighting%2C%20professional%20headshot%20style&width=80&height=80&seq=customer-1&orientation=squarish"
                  alt="お客様"
                  className="w-12 h-12 rounded-full object-cover object-top mr-3"
                />
                <div>
                  <h4 className="font-bold text-gray-800">30代後半・会社員</h4>
                  <p className="text-sm text-gray-600">秋田市在住</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                以前はペット可の賃貸に住んでいましたが、犬の鳴き声や足音に気を使いながらの生活にストレスを感じていました。
                マイペットハウスにしてからは、防音性が高く安心して爱犬と過ごす毎日に。
                掃除も数分で終わり、光熱費も以前より減額されました。
                「私とこの子だけの空間」ができたことで、仕事の疲れも癒されます。
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20woman%20in%20her%2040s%2C%20business%20consultant%2C%20gentle%20smile%2C%20elegant%20attire%2C%20modern%20office%20background%2C%20soft%20professional%20lighting%2C%20approachable%20demeanor&width=80&height=80&seq=customer-2-new&orientation=squarish"
                  alt="お客様"
                  className="w-12 h-12 rounded-full object-cover object-top mr-3"
                />
                <div>
                  <h4 className="font-bold text-gray-800">40代前半・フリーランス</h4>
                  <p className="text-sm text-gray-600">大仙市在住</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                在宅ワーク中心の生活で、猫と静かに暮らせる家を探していました。
                広すぎず狭すぎず、9坪というサイズ感が本当にちょうどいいです。
                断熱がしっかりしていて夏も冬も快適。
                家具を大事にして、好きなインテリアを楽しめるのもお気に入り。
                「小さな家に猫と暮らす」という夢が、ついに実現しました。
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20woman%20in%20her%2030s%2C%20business%20professional%2C%20confident%20smile%2C%20modern%20attire%2C%20office%20background%2C%20professional%20lighting&width=80&height=80&seq=customer-3&orientation=squarish"
                  alt="お客様"
                  className="w-12 h-12 rounded-full object-cover object-top mr-3"
                />
                <div>
                  <h4 className="font-bold text-gray-800">30代前半・転勤族</h4>
                  <p className="text-sm text-gray-600">秋田市在住</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                転勤が多くて家を持つのを諦めていましたが、マイペットハウスは移設できると聞いて決めました。
                設置もたった1日で完了。
                小型犬との生活も始めやすく、休日は庭で一緒に日向ぼっこしています。
                「持ち運べるマイホーム」って、こんなにも気楽で自由なのだと感じます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Form */}
      <section id="reservation-form" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">モデルハウス見学予約</h3>
          <div className="max-w-2xl mx-auto bg-pink-50 rounded-lg p-8">
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="山田 花子"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="090-1234-5678"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  ご質問、ご要望
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="見学希望日時やご質問などをお聞かせください"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  <a href="/privacy" className="text-pink-600 hover:underline cursor-pointer">
                    個人情報の取り扱いについて
                  </a>
                  をご確認の上、送信してください。
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-pink-500 text-white px-8 py-3 rounded-full text-lg hover:bg-pink-6 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : 'モデルハウス予約'}
                </button>
                {submitStatus && (
                  <p className={`mt-4 text-sm ${submitStatus.includes('ありがとう') ? 'text-green-600' : 'text-red-600'}`}>
                    {submitStatus}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">会社紹介</h3>
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-gray-800 mb-4">会社概要</h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex">
                    <dt className="font-medium text-gray-700 w-24">運営会社：</dt>
                    <dd className="text-gray-600">湯沢開発株式会社</dd>
                  </div>
                  <div className="flex">
                    <dt className="font-medium text-gray-700 w-24">代表者：</dt>
                    <dd className="text-gray-600">高橋正安</dd>
                  </div>
                  <div className="flex">
                    <dt className="font-medium text-gray-700 w-24">所在地：</dt>
                    <dd className="text-gray-600">〒012-0844 秋田県湯沢市田町2-１-3</dd>
                  </div>
                  <div className="flex">
                    <dt className="font-medium text-gray-700 w-24">電話番号：</dt>
                    <dd className="text-gray-600">0183-73-7125</dd>
                  </div>
                  <div className="flex">
                    <dt className="font-medium text-gray-700 w-24">営業時間：</dt>
                    <dd className="text-gray-600">9：00～17：00</dd>
                  </div>
                  <div className="flex">
                    <dt className="font-medium text-gray-7

  70">創立：</dt>
                    <dd className="text-gray-600">1964年11月（創業1951年）</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-800 mb-4">許可・資格</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <h5 className="font-medium text-gray-700">許可・登録</h5>
                    <ul className="text-gray-600 text-xs space-y-1 ml-2">
                      <li>• 宅地建物取引業許可：秋田県知事免許(16)第43号</li>
                      <li>• 建設業登録：秋田県知事許可（般-4）第80084号</li>
                      <li>• 一級建築士事務所：秋田県知事登録 第16-10A-0250号</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-700">有資格者</h5>
                    <ul className="text-gray-600 text-xs space-y-1 ml-2">
                      <li>• 一級建築士（1名）</li>
                      <li>• 二級建築士（1名）</li>
                      <li>• 宅地建物取引主任者（7名）</li>
                      <li>• 公認不動産コンサルティングマスター（1名）</li>
                      <li>• 賃貸不動産経営管理士（1名）</li>
                      <li>• 住宅ローンアドバイザー（2名）</li>
                      <li>• ファイナンシャルプランナー（1名）</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-pink-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium">
                1951年創業　75年の実績をもつ住宅会社
              </p>
              <p className="text-xs text-gray-600 mt-1">
                瑕疵保証保険適用・建設業許可・確認申請対応済みで安心してお任せいただけます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Access Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">アクセス</h3>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps?ll=39.160666,140.491807&z=16&t=m&hl=ja&gl=JP&mapclient=embed&q=%E3%80%92012-0844+%E7%A7%8B%E7%94%B0%E7%9C%8C%E6%B9%AF%E6%B2%A2%E5%B8%82%E7%94%B0%E7%94%BA%EF%BC%92%E4%B8%81%E7%9B%AE%EF%BC%91%E2%88%92%EF%BC%93&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="湯沢開発株式会社の地図"
              ></iframe>
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                〒012-0844 秋田県湯沢市田町２丁目１番３号<br />
                TEL: 0183-73-7125
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif' }}>
              マイペットハウス
            </h2>
            <p className="text-gray-300 mb-6">
              あなたとペットだけの安心できる住まい
            </p>
            <div className="flex justify-center gap-4 mb-8">
              <button 
                onClick={() => document.getElementById('reservation-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-pink-500 text-white px-6 py-2 rounded-full hover:bg-pink-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                モデルハウス見学予約
              </button>
            </div>
            <div className="border-t border-gray-700 pt-6">
              <p className="text-gray-400 text-sm">
                Copyright© 2025 湯沢開発株式会社 All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
